// web/src/lib/people.js
// A colour for each person, and the same one every time.
//
// The roster used to colour a shift by the kind of work, which answers "who is
// on morning feed" but not the question Taylor actually asked of it, which was
// "when am I at the barn, and when is Dana". A person's block should be that
// person's colour, on every week and every phone, without anybody assigning it.
//
// So the colour comes from the user id rather than from position in a list —
// the availability calendar numbers its trainers by position, and the way you
// find out that was wrong is somebody turning a different colour the week a
// new groom starts.
//
// Six colours (slate is the colour of "nobody"), handed out across the barn's
// staff so that nobody shares one until there are more than six people. A
// hash alone put Taylor and Luis both in green on the first try, which is the
// one outcome that defeats the purpose.
//
// A chosen colour per person would need a column and a picker, and this is
// what you get before that exists.

import { COLOR_TOKENS, colorClass } from './lessonTypes.js';

const PEOPLE_TOKENS = COLOR_TOKENS.filter((t) => t !== 'slate');

/** FNV-1a. Not cryptographic; it only has to agree with itself across reloads. */
function hash(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/**
 * A colour for each of these people, none shared while there are six or fewer.
 *
 * Each person asks for the colour their id hashes to and takes the next free
 * one if it is gone, in id order — so the result is the same on every device
 * and every week for the same staff, and a new hire only ever moves somebody
 * whose first choice they happened to share.
 */
export function assignPersonColors(userIds = []) {
  const ids = [...new Set(userIds.filter(Boolean))].sort();
  const taken = new Set();
  const out = new Map();
  for (const id of ids) {
    const want = hash(id) % PEOPLE_TOKENS.length;
    let pick = PEOPLE_TOKENS[want];
    if (taken.size < PEOPLE_TOKENS.length) {
      for (let k = 0; k < PEOPLE_TOKENS.length; k++) {
        const t = PEOPLE_TOKENS[(want + k) % PEOPLE_TOKENS.length];
        if (!taken.has(t)) { pick = t; break; }
      }
    }
    taken.add(pick);
    out.set(id, pick);
  }
  return out;
}

/** The token for one person, on their own. Stable for the life of the id. */
export function personColor(userId) {
  if (!userId) return 'slate';
  return PEOPLE_TOKENS[hash(userId) % PEOPLE_TOKENS.length];
}

/** Classes for a person's colour. Pass the map from assignPersonColors when
 *  there is a roster to hand colours out across; the lone hash otherwise. */
export function personClass(userId, variant = 'card', colors = null) {
  const token = userId ? (colors?.get(userId) ?? personColor(userId)) : 'slate';
  return colorClass(token, variant);
}
